<script lang="ts">
  /**
   * The estate register — and, more importantly, the sheet it prints.
   *
   * ## Why the print view is the point
   *
   * A digital vault is no use to the person who needs it most. If Christo dies,
   * Mandri is grieving, possibly at a bank counter, possibly on someone else's
   * phone, and the last thing that will help is "sign in to the portal". So this
   * screen exists to be **printed once and kept with the physical will**. The
   * screen is how you maintain it; the paper is how it gets used.
   *
   * ## Why `originalLocation` is the first column
   *
   * For the documents that matter here the scan is not the instrument. South
   * African law wants the **original signed will** — the Master of the High Court
   * will not accept a copy without a court application, because a missing
   * original raises a presumption that it was deliberately destroyed. The same is
   * true of an antenuptial contract.
   *
   * So Vault's job is not to *be* the will. It is to record that one exists, where
   * the signed original physically is, and who to ring. That is the fact nobody
   * writes down and the one a scan can never supply.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type EstateDoc = {
    id: string;
    title: string;
    docType: string;
    hasFile: boolean;
    originalLocation: string | null;
    contact: { name: string; role: string; phone: string | null } | null;
    reference: string | null;
    urgency: "first" | "later";
    notes: string | null;
  };

  const { pretty }: { pretty: (s: string) => string } = $props();

  let first = $state<EstateDoc[]>([]);
  let later = $state<EstateDoc[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  const total = $derived(first.length + later.length);
  /** Rows with no recorded original location — the gap that matters most. */
  const missingLocation = $derived([...first, ...later].filter((d) => !d.originalLocation).length);

  async function load() {
    loading = true;
    try {
      const r = await api.get<{ first: EstateDoc[]; later: EstateDoc[] }>("/estate");
      first = r.first;
      later = r.later;
      err = null;
    } catch (e) {
      err = e instanceof ApiError ? e.message : "Could not load the estate register.";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });
</script>

<div class="wrap">
  <!-- Screen-only preamble. The printed sheet gets its own header below. -->
  <section class="card screen-only">
    <div class="sec">
      <span class="kicker">Estate register</span>
      <button class="btn-primary" onclick={() => window.print()} disabled={total === 0}>
        <Icon name="printer" size={14} /> Print the sheet
      </button>
    </div>
    <p class="sub">
      What someone needs if something happens to you. <strong>Print this and keep it with the
      physical will</strong> — whoever needs it may not be able to sign in, and that is exactly
      the moment they will need it.
    </p>
    {#if missingLocation > 0}
      <div class="attn attn--warn">
        <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
        <span class="attn__text">
          {missingLocation} of {total}
          {missingLocation === 1 ? "entry has" : "entries have"} no recorded location for the signed
          original. For a will or an antenuptial contract that is the one fact the scan cannot replace.
        </span>
      </div>
    {/if}
  </section>

  {#if loading}
    <p class="sub pad screen-only">Loading…</p>
  {:else if err}
    <p class="sub pad screen-only"><Icon name="alert" size={12} /> {err}</p>
  {:else if total === 0}
    <section class="card screen-only">
      <p class="sub pad">
        Nothing is marked as an estate document yet. Open a document, tick <em>estate</em>, and record
        where the signed original is kept — that is the part that matters.
      </p>
    </section>
  {:else}
    <!-- ── The printed sheet ───────────────────────────────────────────── -->
    <article class="sheet">
      <header class="sheet__head">
        <h1>If something happens to Christo</h1>
        <p class="sheet__sub">
          Where the important documents are. Printed {dateZA(Date.now(), "long")}.
        </p>
        <p class="sheet__warn">
          The <strong>signed originals</strong> are what count legally — a scan of a will is not the
          will. This sheet says where each original is kept.
        </p>
      </header>

      {@render band("Needed within days", first)}
      {@render band("Needed within a fortnight", later)}

      <footer class="sheet__foot">
        Kept in the Vault. If you can sign in, everything here is also at
        <strong>vault.helloliam.co.za</strong>.
      </footer>
    </article>
  {/if}
</div>

{#snippet band(heading: string, rows: EstateDoc[])}
  {#if rows.length > 0}
    <section class="band">
      <h2>{heading}</h2>
      {#each rows as d (d.id)}
        <div class="row">
          <div class="row__title">
            {d.title}
            <span class="row__type">{pretty(d.docType)}</span>
            {#if !d.hasFile}<span class="row__norec">record only — no scan</span>{/if}
          </div>

          <dl>
            <!-- First, deliberately: it is the fact the scan cannot supply. -->
            <dt>Original kept at</dt>
            <dd class:missing={!d.originalLocation}>
              {d.originalLocation ?? "— not recorded —"}
            </dd>

            {#if d.contact}
              <dt>Who to call</dt>
              <dd>
                {d.contact.name}{d.contact.role ? `, ${d.contact.role}` : ""}
                {#if d.contact.phone}<span class="num"> · {d.contact.phone}</span>{/if}
              </dd>
            {/if}

            {#if d.reference}
              <dt>Reference</dt>
              <dd class="num">{d.reference}</dd>
            {/if}

            {#if d.notes}
              <dt>Note</dt>
              <dd>{d.notes}</dd>
            {/if}
          </dl>
        </div>
      {/each}
    </section>
  {/if}
{/snippet}

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .card {
    padding: 14px 16px;
  }
  .sec {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 8px;
  }
  .sec .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .pad {
    padding: 8px 2px;
  }

  .sheet {
    background: var(--s1);
    border-radius: var(--r-surface);
    padding: 22px 24px;
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .sheet__head h1 {
    margin: 0 0 4px;
    font-size: 1.1875rem;
    font-weight: 800;
    color: var(--tx);
  }
  .sheet__sub {
    margin: 0;
    font-size: 0.72rem;
    color: var(--mut);
  }
  .sheet__warn {
    margin: 10px 0 0;
    font-size: 0.75rem;
    color: var(--tx2);
    border-left: 2px solid var(--warn);
    padding-left: 10px;
  }
  .band {
    margin-top: 20px;
  }
  .band h2 {
    margin: 0 0 8px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--mut);
  }
  .row {
    padding: 10px 0;
    border-top: 1px solid var(--line);
  }
  .row__title {
    font-weight: 800;
    font-size: 0.8438rem;
    color: var(--tx);
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }
  .row__type {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }
  .row__norec {
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--warn);
  }
  dl {
    display: grid;
    grid-template-columns: 132px 1fr;
    gap: 2px 12px;
    margin: 6px 0 0;
    font-size: 0.7812rem;
  }
  dt {
    color: var(--mut);
    font-weight: 700;
    font-size: 0.72rem;
  }
  dd {
    margin: 0;
    color: var(--tx2);
  }
  dd.missing {
    color: var(--warn);
    font-weight: 700;
  }
  .sheet__foot {
    margin-top: 18px;
    padding-top: 10px;
    border-top: 1px solid var(--line);
    font-size: 0.72rem;
    color: var(--mut);
  }

  /* ── Print ──────────────────────────────────────────────────────────────
     Black on white, one page where it fits. The dark theme is for the screen;
     paper that arrives grey and ink-hungry does not get printed twice. This
     mirrors Waypoint's print view, which is the same exception for the same
     reason. */
  @media print {
    .screen-only {
      display: none !important;
    }
    :global(html),
    :global(body) {
      background: #fff !important;
    }
    /* The register is a leaf screen; hide the app chrome around it. */
    :global(.shell > .rail),
    :global(.head) {
      display: none !important;
    }
    :global(.shell) {
      display: block !important;
      background: #fff !important;
    }
    :global(.content) {
      padding: 0 !important;
      max-width: none !important;
    }
    .sheet {
      background: #fff;
      box-shadow: none;
      border-radius: 0;
      padding: 0;
      color: #000;
    }
    .sheet__head h1,
    .row__title {
      color: #000;
    }
    .sheet__sub,
    .band h2,
    dt,
    .sheet__foot,
    .row__type {
      color: #444;
    }
    .sheet__warn,
    dd {
      color: #000;
    }
    .sheet__warn {
      border-left: 2px solid #000;
    }
    .row,
    .sheet__foot {
      border-top: 1px solid #999;
    }
    /* A document's details must not split across a page break — half an entry
       is worse than none, because it reads as complete. */
    .row {
      break-inside: avoid;
    }
    .band {
      break-inside: auto;
    }
    dd.missing {
      color: #000;
      font-weight: 700;
    }
    dd.missing::after {
      content: " (nobody recorded this)";
      font-weight: 400;
    }
  }
</style>
